/**
 * Project Status Panel
 *
 * Renders inside TicketDetailModal's Details tab when ticket.isProject === true.
 * Lets a tech set/update the human-curated overlay fields:
 *   - percentComplete (0-100)
 *   - health (green / yellow / red)
 *   - projectStatusNote (one-liner)
 *   - projectUpdates[] (append-only dated log entries)
 *
 * Added for OverSite V3's Project Management surface — V3 pulls these
 * from /api/tickets and maps them to project.percentComplete /
 * project.health / project.updates[].
 *
 * Save is a single PATCH that gets all three scalars at once; the API
 * auto-stamps projectStatusUpdatedAt server-side. The "Add Update" form
 * appends one entry to projectUpdates and PATCHes the whole array.
 */

import { useState } from 'react';
import type { Ticket } from '../services/types';
import { setTicketProjectStatus } from '../services/api';

interface Props {
  ticket: Ticket;
  myResourceId?: number;
  onUpdated?: (patch: Partial<Ticket>) => void;
}

type Health = 'green' | 'yellow' | 'red';

export function ProjectStatusPanel({ ticket, myResourceId, onUpdated }: Props) {
  const [percent, setPercent] = useState<number>(ticket.percentComplete ?? 0);
  const [health, setHealth] = useState<Health | ''>((ticket.health as Health) ?? '');
  const [note, setNote] = useState<string>(ticket.projectStatusNote ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Append-update form state
  const [showAdd, setShowAdd] = useState(false);
  const [updateDate, setUpdateDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [narrative, setNarrative] = useState('');
  const [nextSteps, setNextSteps] = useState('');
  const [blockers, setBlockers] = useState('');
  const [appending, setAppending] = useState(false);

  const dirty =
    percent !== (ticket.percentComplete ?? 0) ||
    health !== (ticket.health ?? '') ||
    note !== (ticket.projectStatusNote ?? '');

  async function saveStatus() {
    if (!dirty) return;
    setSaving(true);
    setError(null);
    try {
      await setTicketProjectStatus(
        ticket.autoTaskId,
        {
          percentComplete: percent,
          health: health || null,
          projectStatusNote: note,
          ...(myResourceId ? { projectStatusUpdatedBy: myResourceId } : {}),
        },
        'project-status panel save',
      );
      onUpdated?.({
        percentComplete: percent,
        health: (health || undefined) as Health | undefined,
        projectStatusNote: note,
        projectStatusUpdatedAt: new Date().toISOString(),
        projectStatusUpdatedBy: myResourceId,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function appendUpdate() {
    if (!narrative.trim() || !myResourceId) return;
    setAppending(true);
    setError(null);
    try {
      const existing = ticket.projectUpdates ?? [];
      const entry = {
        date: updateDate,
        authorResourceId: myResourceId,
        narrative: narrative.trim(),
        ...(nextSteps.trim() ? { nextSteps: nextSteps.trim() } : {}),
        ...(blockers.trim() ? { blockers: blockers.trim() } : {}),
      };
      const projectUpdates = [...existing, entry];
      await setTicketProjectStatus(
        ticket.autoTaskId,
        { projectUpdates },
        'project-status panel append',
      );
      onUpdated?.({ projectUpdates });
      setNarrative('');
      setNextSteps('');
      setBlockers('');
      setShowAdd(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAppending(false);
    }
  }

  const healthDot = (h: Health) =>
    h === 'green' ? 'bg-emerald-500' : h === 'yellow' ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="bg-slate-700/30 border border-slate-600 rounded p-3 shrink-0 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-teal-300 uppercase tracking-wide">Project Status</h3>
        {ticket.projectStatusUpdatedAt && (
          <span className="text-[10px] text-slate-500">
            Updated {new Date(ticket.projectStatusUpdatedAt).toLocaleString()}
          </span>
        )}
      </div>

      <div className="grid grid-cols-12 gap-3 items-end">
        {/* Percent Complete */}
        <div className="col-span-5">
          <label className="block text-[11px] text-slate-400 mb-1">% Complete</label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={percent}
              onChange={e => setPercent(parseInt(e.target.value, 10))}
              className="flex-1 accent-teal-500"
            />
            <input
              type="number"
              min={0}
              max={100}
              value={percent}
              onChange={e => setPercent(Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0)))}
              className="w-14 bg-slate-900 border border-slate-600 rounded px-1.5 py-1 text-white text-xs text-right tabular-nums"
            />
            <span className="text-xs text-slate-400">%</span>
          </div>
        </div>

        {/* Health */}
        <div className="col-span-3">
          <label className="block text-[11px] text-slate-400 mb-1">Health</label>
          <div className="flex gap-1">
            {(['green', 'yellow', 'red'] as Health[]).map(h => (
              <button
                key={h}
                type="button"
                onClick={() => setHealth(health === h ? '' : h)}
                className={`flex-1 px-2 py-1 rounded border text-[11px] flex items-center justify-center gap-1 ${
                  health === h
                    ? 'border-white/40 bg-slate-800 text-white'
                    : 'border-slate-700 bg-slate-900 text-slate-500 hover:text-slate-300'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${healthDot(h)}`}></span>
                {h[0].toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Save */}
        <div className="col-span-4">
          <button
            type="button"
            disabled={!dirty || saving}
            onClick={saveStatus}
            className="w-full px-3 py-1.5 text-xs font-medium rounded bg-teal-600 text-white hover:bg-teal-500 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : dirty ? 'Save status' : 'No changes'}
          </button>
        </div>
      </div>

      {/* Status note */}
      <div>
        <label className="block text-[11px] text-slate-400 mb-1">Status note</label>
        <input
          type="text"
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="One-liner — what's happening right now?"
          className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"
        />
      </div>

      {error && (
        <div className="text-[11px] text-red-400 bg-red-500/10 border border-red-500/30 rounded px-2 py-1">
          {error}
        </div>
      )}

      {/* Updates log */}
      <div className="pt-2 border-t border-slate-700 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-medium text-slate-300">
            Update log ({ticket.projectUpdates?.length ?? 0})
          </span>
          <button
            type="button"
            onClick={() => setShowAdd(s => !s)}
            className="text-[11px] text-teal-400 hover:text-teal-300"
          >
            {showAdd ? 'Cancel' : '+ Add update'}
          </button>
        </div>

        {showAdd && (
          <div className="bg-slate-900/50 border border-slate-700 rounded p-2 space-y-1.5">
            <div className="flex gap-2">
              <input
                type="date"
                value={updateDate}
                onChange={e => setUpdateDate(e.target.value)}
                className="bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-[11px]"
              />
              <input
                type="text"
                value={narrative}
                onChange={e => setNarrative(e.target.value)}
                placeholder="Narrative (what happened)"
                className="flex-1 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-[11px]"
              />
            </div>
            <input
              type="text"
              value={nextSteps}
              onChange={e => setNextSteps(e.target.value)}
              placeholder="Next steps (optional)"
              className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-[11px]"
            />
            <input
              type="text"
              value={blockers}
              onChange={e => setBlockers(e.target.value)}
              placeholder="Blockers (optional)"
              className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-[11px]"
            />
            <div className="flex justify-end">
              <button
                type="button"
                disabled={!narrative.trim() || !myResourceId || appending}
                onClick={appendUpdate}
                className="px-3 py-1 text-[11px] font-medium rounded bg-teal-600 text-white hover:bg-teal-500 disabled:bg-slate-700 disabled:text-slate-500"
              >
                {appending ? 'Saving…' : 'Append'}
              </button>
            </div>
          </div>
        )}

        {ticket.projectUpdates && ticket.projectUpdates.length > 0 && (
          <div className="max-h-40 overflow-y-auto space-y-1.5">
            {[...ticket.projectUpdates].reverse().map((u, i) => (
              <div key={i} className="text-[11px] bg-slate-900/30 border border-slate-700/50 rounded px-2 py-1">
                <div className="flex justify-between text-slate-500">
                  <span>{u.date}</span>
                  <span>resourceId {u.authorResourceId}</span>
                </div>
                <div className="text-slate-300">{u.narrative}</div>
                {u.nextSteps && (
                  <div className="text-slate-400">
                    <span className="text-slate-500">Next: </span>{u.nextSteps}
                  </div>
                )}
                {u.blockers && (
                  <div className="text-amber-300">
                    <span className="text-slate-500">Blockers: </span>{u.blockers}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
